/**
 * iter-170al — Public invite-accept page.
 *
 * Mounted at ``/invite/:token``. Resolves the invite without auth so
 * a logged-out user sees what they were invited to BEFORE having to
 * sign in. If they're already logged in, "Accept" calls the
 * authenticated endpoint; if not, we redirect them to login with a
 * ``next`` param pointing back here.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Users, AlertCircle, CheckCircle2, LogIn } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function isLoggedIn() {
  return !!localStorage.getItem("token");
}

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/invites/${token}`);
        if (!cancelled) setInvite(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "invitation_not_found");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const accept = async () => {
    if (!isLoggedIn()) {
      // Bounce through login; bring them back here after auth.
      navigate(`/login?next=/invite/${token}`);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(`/invites/${token}/accept`);
      setAccepted(true);
      toast.success("Invitación aceptada");
      setTimeout(() => navigate("/workspace"), 1000);
      void res;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "error");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="p-8 max-w-md text-center" data-testid="invite-error">
          <div className="h-12 w-12 rounded-full bg-rose-100 text-rose-700 mx-auto mb-3 flex items-center justify-center">
            <AlertCircle className="h-5 w-5" />
          </div>
          <h1 className="font-display text-xl font-bold text-slate-900 mb-2">Invitación no válida</h1>
          <p className="text-sm text-slate-600">
            {error === "invitation_expired" ? "Esta invitación expiró. Pide una nueva."
              : error === "invitation_revoked" ? "La invitación fue revocada."
              : error === "invitation_already_accepted" ? "Ya aceptaste esta invitación. Inicia sesión para entrar al espacio."
              : "El link no existe o expiró."}
          </p>
          <Button onClick={() => navigate("/login")} variant="outline" className="mt-4 gap-1.5">
            <LogIn className="h-3.5 w-3.5" /> Iniciar sesión
          </Button>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="p-8 max-w-md text-center" data-testid="invite-accepted">
          <div className="h-12 w-12 rounded-full bg-emerald-100 text-emerald-700 mx-auto mb-3 flex items-center justify-center">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <h1 className="font-display text-xl font-bold text-slate-900 mb-2">¡Bienvenido!</h1>
          <p className="text-sm text-slate-600">Te llevamos al espacio…</p>
        </Card>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Cargando…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="p-8 max-w-md" data-testid="invite-page">
        <div className="h-12 w-12 rounded-full bg-sky-100 text-sky-700 mx-auto mb-4 flex items-center justify-center">
          <Users className="h-6 w-6" />
        </div>
        <h1 className="font-display text-xl font-bold text-slate-900 text-center mb-2">
          Te invitaron a <span className="text-sky-700">{invite.space_name}</span>
        </h1>
        <p className="text-sm text-slate-600 text-center mb-1">
          {invite.inviter?.email && (
            <>
              <strong>{invite.inviter.name || invite.inviter.email}</strong> te invitó como{" "}
            </>
          )}
          <span className="font-semibold uppercase text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
            {invite.role}
          </span>
        </p>
        <p className="text-xs text-slate-500 text-center mb-6">
          Cuenta: <strong>{invite.email}</strong>
        </p>
        <div className="flex gap-2 justify-center">
          <Button
            onClick={accept}
            disabled={busy}
            className="bg-sky-600 hover:bg-sky-700 gap-1.5"
            data-testid="invite-accept-btn"
          >
            {busy ? "Aceptando…" : isLoggedIn() ? "Aceptar invitación" : "Iniciar sesión y aceptar"}
          </Button>
          <Button variant="outline" onClick={() => navigate("/")} data-testid="invite-cancel-btn">
            Después
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 text-center mt-4">
          Lumfi Cash Pilot · El link expira a los 14 días
        </p>
      </Card>
    </div>
  );
}
